<template>
  <nav :aria-label="ariaLabelLowerNav" class="flex justify-between w-full gap-8 ellevio-desktop-nav-lower mt-[1px] z-10">
    <!-- Main nav -->
    <ul class="flex gap-8 items-center" id="main-menu">
      <li v-for="(item, index) in navigationData.menuItems" :key="`menuItem-${index}`" role="none" class="relative pt-4 pb-3">
        <ellevio-web-desktop-nav-lower-mega-menu-item
          :label="item.heading"
          :itemId="`menuItem-${index}`"
          :itemIcon="item.icon"
          :itemIsOpen="activeMenuItem === `menuItem-${index}`"
          :showActiveUnderline="showActiveUnderline(activeMenuItem === `menuItem-${index}`, item)"
          :headerHeight="headerHeight"
          :scrollbarWidth="scrollbarWidth"
          @toggleMegaMenu="handleToggleMegaMenu"
          :ariaLabels="translations?.ariaLabels">
          <!-- Heading link -->
          <div class="flex gap-5 items-center mb-8" :data-testid="`desktopMegaMenuHeadingWrapper-${index}`">
            <ellevio-web-desktop-nav-lower-nav-icon :typeOfIcon="item.icon" :large="true" />
            <div>
              <h2 class="text-3xl font-futuraEfDemiBold" :data-testid="`desktopMegaMenuHeading-${index}`">{{ item.heading }}</h2>
              <p v-if="item.description" class="mt-1 text-lg font-futuraEfBook text-newColors-gray-contentA" :data-testid="`desktopMegaMenuParagraph-${index}`">{{ item.description }}</p>
            </div>
          </div>

          <!-- Sub menu items -->
          <ul class="grid grid-cols-12 auto-rows-auto gap-x-8 gap-y-14">
            <li class="col-span-3 grid-item" v-for="(subitem, subIndex) in item.nodes" :key="`subMenuItem-${subIndex}`">
              <h3 class="mb-2 text-xl font-futuraEfDemiBold" :data-testid="`desktopMegaMenuSubHeading-${index}`">
                <a v-if="subitem.url" :href="subitem.url" class="text-xl font-futuraEfDemiBold" :data-testid="`desktopMegaMenuSubHeadingLink-${index}`" :class="[{ underline: subitem.isSelected }]">{{ subitem.text }}</a>
                {{ !subitem.url ? subitem.text : '' }}
              </h3>
              <ul class="flex flex-col gap-2 text-newColors-gray-contentA ellevio-mega-menu-submenu" role="menu">
                <li v-for="(subsubitem, subsubindex) in subitem.items" :key="`subSubMenuItem-${subsubindex}`" role="none">
                  <a
                    :href="subsubitem.url"
                    :target="subsubitem.target === '' ? '_self' : subsubitem.target"
                    role="menuitem"
                    class="rounded-sm"
                    :class="[{ underline: subsubitem.isSelected }]"
                    :data-testid="`desktopMegaMenuSubMenuLink-${index}-${subIndex}-${subsubindex}`"
                    >{{ subsubitem.text }}</a
                  >
                </li>
              </ul>
            </li>
          </ul>
        </ellevio-web-desktop-nav-lower-mega-menu-item>
      </li>
    </ul>

    <div class="flex gap-5 items-center">
      <!-- Search -->
      <div class="pt-4 pb-3">
        <ellevio-web-desktop-nav-lower-mega-menu-item
          :label="translations?.search?.menuLabel"
          :searchLink="true"
          :itemId="'headerSearch'"
          :showActiveUnderline="showActiveUnderline(activeMenuItem === 'headerSearch')"
          :itemIsOpen="activeMenuItem === 'headerSearch'"
          @toggleMegaMenu="handleToggleMegaMenu"
          :headerHeight="headerHeight"
          :aria-labels="translations?.ariaLabels">
          <form name="searchform" :action="searchPageUrl">
            <ellevio-web-header-search-input
              :searchNoHitsContent="searchNoHitsContent"
              :apiUrl="searchApiUrl"
              :clearSearchFieldLabel="translations?.search?.clearSearchField"
              :label="translations?.search?.inputLabel"
              :searchTooltipLabel="translations?.search?.inputLabel"
              :buttonText="translations?.search?.buttonText"
              :itemIsOpen="activeMenuItem === 'headerSearch'"
              :placeholder="translations?.search?.inputPlaceholder" />
          </form>
        </ellevio-web-desktop-nav-lower-mega-menu-item>
      </div>

      <!-- Login -->
      <div v-if="navigationData?.loginMenu">
        <ellevio-web-desktop-nav-lower-mega-menu-item
          :label="navigationData?.loginMenu?.userName || navigationData?.loginMenu?.heading"
          :loginLink="!navigationData?.loginMenu?.userName"
          :logoutLink="navigationData?.loginMenu?.userName"
          :itemId="'headerLogin'"
          :itemIsOpen="activeMenuItem === 'headerLogin'"
          @toggleMegaMenu="handleToggleMegaMenu"
          :headerHeight="headerHeight">
          <div class="flex gap-5 items-center mb-8" data-testid="desktopMegaMenuHeadingWrapper-login">
            <ellevio-web-desktop-nav-lower-nav-icon typeOfIcon="user" />
            <div>
              <h2 class="text-3xl font-futuraEfDemiBold" data-testid="desktopMegaMenuHeading-login">{{ navigationData?.loginMenu?.heading }}</h2>
              <p class="text-lg font-futuraEfBook" data-testid="desktopMegaMenuParagraph-login">{{ navigationData?.loginMenu?.description }}</p>
            </div>
          </div>
          <ul class="flex gap-44 items-start" role="menu">
            <li v-for="(area, index) in navigationData?.loginMenu?.areas" :key="`area-${index}`" role="menuitem">
              <h3 v-if="area?.heading" class="mb-1 text-xl font-futuraEfDemiBold" :data-testid="`desktopMegaMenuLoginSubHeading-${index}`">{{ area?.heading }}</h3>
              <ul class="flex flex-col gap-2" role="menu">
                <li v-for="(link, index2) in area?.links" :key="`area-link-${index2}`" role="menuitem">
                  <a :href="link.url" :target="link.target === '' ? '_self' : link.target" class="rounded-sm arrow-link-forward" :data-testid="`desktopMegaMenuLoginSubMenuLink-${index}-${index2}`">{{ link.text }}</a>
                </li>
              </ul>
            </li>
            <!-- Logout button -->
            <li v-if="navigationData?.loginMenu?.userName" role="menuitem">
              <ul class="flex flex-col gap-2" role="menu">
                <li role="menuitem">
                  <a :href="navigationData?.loginMenu?.logOutButton?.url" class="mt-7 btn-secondary btn-small btn-icon-user">{{ navigationData?.loginMenu?.logOutButton?.text }}</a>
                </li>
              </ul>
            </li>
          </ul>
        </ellevio-web-desktop-nav-lower-mega-menu-item>
      </div>
    </div>
  </nav>
</template>

<script>
import EllevioWebDesktopNavLowerMegaMenuItem from './DesktopNavLowerMegaMenuItem.vue'
import EllevioWebDesktopNavLowerNavIcon from './DesktopNavLowerNavIcon.vue'
import EllevioWebHeaderSearchInput from './HeaderSearchInput.vue'

export default {
  name: 'ellevio-web-desktop-nav-lower',
  components: {
    EllevioWebDesktopNavLowerMegaMenuItem,
    EllevioWebDesktopNavLowerNavIcon,
    EllevioWebHeaderSearchInput
  },
  emits: ['toggleMegaMenu', 'megaMenuIsOpen'],
  props: {
    searchNoHitsContent: {
      type: String,
      default: ''
    },
    headerHeight: {
      type: Number,
      default: 0
    },
    scrollbarWidth: {
      type: Number,
      default: 0
    },
    navigationData: {
      type: Object,
      default: () => {}
    },
    translations: {
      type: Object,
      default: () => {
        return {
          search: {}
        }
      }
    },
    activeMenuItem: {
      type: String,
      default: ''
    },
    ariaLabelLowerNav: {
      type: String,
      default: 'Huvudmeny'
    },
    searchApiUrl: {
      type: String,
      default: ''
    },
    searchPageUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {}
  },
  watch: {
    activeMenuItem: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('megaMenuIsOpen', newVal !== '')
      }
    }
  },
  methods: {
    handleToggleMegaMenu(itemId) {
      this.$emit('toggleMegaMenu', itemId)
    },
    anyChildrenActive(items) {
      return items.some((child) => child.isSelected === true) || items.some((child) => child.items.some((grandchild) => grandchild.isSelected === true))
    },
    showActiveUnderline(itemIsOpen, item) {
      const menuIsClosedAndItemHasChild = item ? this.activeMenuItem === '' && this.anyChildrenActive(item.nodes ?? []) : false
      const menuIsOpen = itemIsOpen

      return menuIsOpen || menuIsClosedAndItemHasChild
    }
  }
}
</script>

<style scoped>
.ellevio-desktop-nav-lower:deep(.animated-menu-link::before) {
  bottom: -0.75rem;
}
</style>
